import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { motion } from 'framer-motion';
import {
  FiArrowLeft,
  FiUser,
  FiClipboard,
  FiDollarSign,
  FiFileText,
  FiFilePlus,
  FiThumbsUp,
  FiThumbsDown,
  FiCheckSquare,
  FiEdit3,
  FiHelpCircle,
  FiAlertCircle,
  FiCheckCircle,
  FiInfo,
  FiMaximize,
  FiMinimize,
  FiX,
  FiCalendar,
  FiActivity,
  FiPaperclip,
  FiShield,
  FiBarChart2,
  FiList,
  FiFolder,
  FiClock,
  FiDownload,
  FiFile,
  FiImage,
  FiVideo,
  FiMusic,
  FiMinus,
  FiPlus,
  FiMaximize2,
  FiChevronDown,
  FiChevronUp,
  FiMail,
  FiMessageSquare,
  FiPhone,
  FiSmartphone,
  FiUsers
} from 'react-icons/fi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import {
  PageContainer,
  PageHeader,
  PageTitle,
  PageIcon,
  Button,
  Card,
  CardTitle,
  CardContent,
  StatusBadge
} from '../styles/PageStyles';
import { getClaimById, updateClaimStatus } from '../services/claimService';
import { getPipelineById, getPipelineRuns, resumePipeline, getNodesForPipeline } from '../services/pipelineService';
import { fetchEntityByPagination, listAllEntities, fetchCountByFilter } from '../services/entityService';
import { downloadImage, listFiles } from '../services/fileUploadService';
import PipelineExecutionDrawer from '../components/pipeline/PipelineExecutionDrawer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHurricane } from '@fortawesome/free-solid-svg-icons';

// Styled Components for ClaimDetails
const ClaimDetailsContainer = styled.div`
  display: flex;
  gap: 1.5rem;
  height: calc(100vh - 12rem);
  transition: all 0.3s ease;
`;

const DetailsSection = styled.div`
  flex: ${props => props.hidden ? '0' : '1'};
  display: ${props => props.hidden ? 'none' : 'flex'};
  flex-direction: column;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: flex 0.3s ease;
  padding: 0;
`;

const ActionsSection = styled.div`
  flex: ${props => props.expanded ? '1' : '0.6'};
  display: flex;
  flex-direction: column;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-left: ${props => props.expanded ? '0' : '1rem'};
  overflow: hidden;
  transition: flex 0.3s ease, margin-left 0.3s ease;
  position: relative;
  background: #f8fafc;
  height: calc(100vh - 12rem); /* Ensure it has a fixed height */
  max-height: calc(100vh - 12rem);
`;

const ActionsSectionHeader = styled.div`
  padding: 0.5rem 1.5rem 0.5rem 0.2rem;
  border-bottom: 1px solid #e2e8f0;
`;

const ActionsSummary = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  background: rgba(0, 102, 255, 0.03);
`;

const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const SummaryValue = styled.div`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--primary);
`;

const SummaryLabel = styled.div`
  font-size: 0.8rem;
  color: var(--text-secondary);
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  margin: 0.5rem 1.5rem 1.5rem 1.5rem;
  position: relative;
  overflow: hidden;
`;

const ProgressIndicator = styled.div`
  position: absolute;
  height: 100%;
  background: var(--primary);
  border-radius: 4px;
  transition: width 0.5s ease;
`;

const ProgressMarker = styled.div`
  position: absolute;
  width: 3px;
  height: 16px;
  background: ${props => props.color || '#64748b'};
  top: -4px;
  border-radius: 1.5px;
`;

const ExpandButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  color: var(--text-primary);
  background-color: transparent;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    background-color: var(--primary);
    color: white;
  }

  svg {
    font-size: 1.2rem;
  }
`;

const TimelineContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  overflow-y: auto;
  max-height: 330px;
  padding-right: 2px;
  width: 100%;
  box-sizing: border-box;
  scrollbar-width: thin;
  scrollbar-color: var(--border-color) transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--border-color);
    border-radius: 3px;
  }
`;

const TimelineItem = styled(motion.div)`
  display: flex;
  border-radius: 0.5rem;
  margin-bottom: 1.5rem;
  background: ${props => props.isExpanded ? 'var(--bg-highlight)' : 'transparent'};
  position: relative;
  width: 100%;
  box-sizing: border-box;
  
  &:not(:last-child):after {
    content: '';
    position: absolute;
    top: 2rem;
    left: 1rem;
    bottom: -1.5rem;
    width: 2px;
    background: #e2e8f0;
  }
`;

const TimelineIconContainer = styled(motion.div)`
  min-width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: ${props => props.bg || '#e2e8f0'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  color: ${props => props.color || 'var(--text-secondary)'};
  margin-right: 1rem;
  z-index: 1;
  box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.7);
`;

const TimelineContent = styled.div`
  flex: 1;
  width: 100%;
  min-width: 0; /* Prevents flex items from overflowing */
  overflow-wrap: break-word;
  word-wrap: break-word;
`;

const TimelineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.25rem;
`;

const TimelineTitle = styled.div`
  font-weight: 500;
  color: var(--text-primary);
`;

const TimelineTime = styled.div`
  font-size: 0.8rem;
  color: var(--text-secondary);
`;

const TimelineDescription = styled.div`
  font-size: 0.9rem;
  color: var(--text-secondary);
  margin-bottom: 0.5rem;
  width: 100%;
  overflow-wrap: break-word;
  word-wrap: break-word;
  
  /* Style for ReactMarkdown content */
  & > div {
    width: 100%;
  }
  
  & p {
    margin: 0.5rem 0;
  }
  
  & code {
    background-color: var(--bg-secondary);
    padding: 0.1rem 0.3rem;
    border-radius: 0.25rem;
    font-family: monospace;
  }
`;

const TimelineDetails = styled(motion.div)`
  font-size: 0.85rem;
  padding: 0.75rem;
  background: white;
  border-radius: 0.5rem;
  color: var(--text-secondary);
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
`;

const AgentAvatar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const AvatarIcon = styled.div`
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  background: ${props => props.bg || '#e2e8f0'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  color: white;
`;

const AgentMetrics = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px dashed #e2e8f0;
`;

const MetricItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const MetricValue = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const MetricLabel = styled.div`
  font-size: 0.7rem;
  color: var(--text-secondary);
`;

const TabsContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #e2e8f0;
`;

const Tab = styled.div`
  padding: 1rem 0.9rem;
  font-size: 0.9rem;
  font-weight: ${props => props.active ? '600' : '400'};
  color: ${props => props.active ? 'var(--primary)' : 'var(--text-secondary)'};
  border-bottom: 2px solid ${props => props.active ? 'var(--primary)' : 'transparent'};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  &:hover {
    color: var(--primary);
    background: rgba(0, 102, 255, 0.05);
  }
`;

const TabDropdownContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const TabDropdownTrigger = styled.div`
  padding: 1rem 0.9rem;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border-bottom: 2px solid ${props => props.hasActiveTab ? 'var(--primary)' : 'transparent'};
  
  &:hover {
    color: var(--primary);
    background: rgba(0, 102, 255, 0.05);
  }
`;

const TabDropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 200px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  display: ${props => props.isOpen ? 'block' : 'none'};
  max-height: 300px;
  overflow-y: auto;
`;

const TabDropdownItem = styled.div`
  padding: 0.75rem 1rem;
  font-size: 0.9rem;
  color: ${props => props.active ? 'var(--primary)' : 'var(--text-primary)'};
  background: ${props => props.active ? 'rgba(0, 102, 255, 0.1)' : 'transparent'};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border-bottom: 1px solid #f1f5f9;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: rgba(0, 102, 255, 0.1);
    color: var(--primary);
  }
`;

const TabContent = styled.div`
  padding: 1.5rem;
  overflow-y: auto;
  height: 100%;
`;

const ClaimInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const InfoLabel = styled.div`
  font-size: 0.8rem;
  color: var(--text-secondary);
`;

const InfoValue = styled.div`
  font-size: 0.95rem;
  font-weight: 500;
  color: var(--text-primary);
`;

const DocumentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const DocumentItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid #e2e8f0;
  cursor: ${props => props.hasLink ? 'pointer' : 'default'};
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${props => props.hasLink ? '#f1f5f9' : 'transparent'};
    cursor: ${props => props.hasLink ? 'pointer' : 'default'};
  }
`;

const DocumentIcon = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.5rem;
  background: #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  color: var(--text-secondary);
`;

const DocumentInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const DocumentTitle = styled.div`
  font-weight: 500;
  color: var(--text-primary);
`;

const DocumentMeta = styled.div`
  font-size: 0.8rem;
  color: var(--text-secondary);
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  background: ${props => props.active ? 'var(--primary)' : 'transparent'};
  color: ${props => props.active ? 'white' : 'var(--text-secondary)'};
  font-size: 1.1rem;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s;
  position: relative;

  &:hover {
    background: ${props => props.active ? 'var(--primary)' : '#f1f5f9'};
  }

  &:hover::after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: -30px;
    left: 50%;
    transform: translateX(-50%);
    background: #1e293b;
    color: white;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 0.75rem;
    white-space: nowrap;
    z-index: 1000;
  }
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  gap: 0.2rem;
`;

const MarkdownContainer = styled.div`
  padding: 1rem;
  background: var(--bg-secondary);
  border-radius: 0.5rem;
  margin-bottom: 1rem;
  max-height: 500px;
  overflow-y: auto;
  h1{
  font-size: 1.4rem;
  }
  h2{
  font-size: 1.3rem;
  }
  h3{
  font-size: 1.2rem;
  }
  h4{
  font-size: 1.1rem;
  }
  /* Table styles for markdown */
  & table {
    border-collapse: collapse;
    width: 100%;
    margin: 1rem 0;
    font-size: 0.9rem;
  }
  
  & th {
    background-color: var(--bg-highlight);
    color: var(--text-primary);
    font-weight: 600;
    text-align: left;
    padding: 0.75rem;
    border: 1px solid var(--border-color);
  }
  
  & td {
    padding: 0.75rem;
    border: 1px solid var(--border-color);
  }
  
  & tr:nth-child(even) {
    background-color: rgba(0, 0, 0, 0.03);
  }
  
  /* Other markdown styles */
  & code {
    background-color: rgba(0, 0, 0, 0.05);
    padding: 0.2rem 0.4rem;
    border-radius: 0.25rem;
    font-family: monospace;
  }
  
  & blockquote {
    border-left: 4px solid var(--primary-color);
    padding-left: 1rem;
    margin-left: 0;
    color: var(--text-secondary);
  }
  
  & ul, & ol {
    padding-left: 2rem;
  }
`;

const FeedbackContainer = styled.div`
  margin-top: 20px;
  border-top: 1px solid var(--border-color);
  padding-top: 15px;
`;

const FeedbackTitle = styled.h4`
  font-size: 0.9rem;
  color: var(--text-primary);
  margin-bottom: 10px;
`;

const FeedbackOptions = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 15px;
`;

const FeedbackOption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 8px 12px;
  font-size: 0.8rem;
  border-radius: 4px;
  background-color: ${props => props.selected ? 'var(--primary-light)' : 'transparent'};
  color: ${props => props.selected ? '#ffffff' : 'var(--text-secondary)'};
  transition: all 0.2s ease;

  &:hover {
    background-color: var(--primary-light);
    color: #ffffff;
  }

  svg {
    font-size: 1.2rem;
  }
`;

const RemarksContainer = styled.div`
  margin-top: 15px;
`;

const RemarksTextarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 10px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  font-family: inherit;
  font-size: 0.9rem;
  resize: vertical;
  margin-bottom: 10px;

  &:focus {
    outline: none;
    border-color: var(--primary);
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  cursor: pointer;
`;

const SubmitButton = styled.button`
  background-color: var(--primary);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 16px;
  font-size: 0.9rem;
  cursor: pointer;
  margin-top: 15px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: var(--primary-dark);
  }

  &:disabled {
    background-color: var(--border-color);
    cursor: not-allowed;
  }
`;

const NotificationContainer = styled.div`
  margin-top: 15px;
  padding: 12px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 10px;
  background-color: ${props => props.type === 'success' ? 'var(--success-light)' : 'var(--error-light)'};
  color: ${props => props.type === 'success' ? 'var(--success)' : 'var(--error)'};
  animation: fadeIn 0.3s ease;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }

  svg {
    font-size: 1.2rem;
  }
`;

const DocumentViewerContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  height: 100%;
  position: ${props => props.isExpanded ? 'fixed' : 'relative'};
  top: ${props => props.isExpanded ? '0' : 'auto'};
  left: ${props => props.isExpanded ? '0' : 'auto'};
  right: ${props => props.isExpanded ? '0' : 'auto'};
  bottom: ${props => props.isExpanded ? '0' : 'auto'};
  z-index: ${props => props.isExpanded ? '1000' : 'auto'};
  width: ${props => props.isExpanded ? '100%' : 'auto'};
  height: ${props => props.isExpanded ? '100vh' : '100%'};
`;

const DocumentViewerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-bottom: 1px solid var(--border-color);
`;

const DocumentViewerTitle = styled.h3`
  font-size: 1rem;
  color: var(--text-primary);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DocumentViewerContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  overflow: auto;
  background-color: #f8fafc;
`;

const DocumentActions = styled.div`
  display: flex;
  gap: 8px;
`;

const DocumentAction = styled.button`
  background: none;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 4px;
  cursor: pointer;
  color: var(--text-secondary);
  
  &:hover {
    background-color: var(--primary-light);
    color: var(--primary);
  }
`;

const DocumentFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  flex: 1;
`;

const DocumentEmbed = styled.embed`
  width: 100%;
  height: 100%;
  border: none;
  flex: 1;
`;

const DocumentVideo = styled.video`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const DocumentAudio = styled.audio`
  width: 100%;
`;

const ZoomableImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  background: #f0f0f0;
`;

const ZoomableImageWrapper = styled.div`
  position: relative;
  cursor: ${props => props.isDragging ? 'grabbing' : 'grab'};
  transform: scale(${props => props.scale}) translate(${props => props.posX}px, ${props => props.posY}px);
  transition: transform ${props => props.isDragging ? '0s' : '0.1s'} ease-out;
`;

const ZoomableImg = styled.img`
  max-width: 100%;
  max-height: 100%;
  user-select: none;
  pointer-events: none;
`;

const ImageControls = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 10px;
  background: rgba(0, 0, 0, 0.5);
  padding: 8px 12px;
  border-radius: 20px;
  z-index: 10;
`;

const ImageControlButton = styled.button`
  background: transparent;
  color: white;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
  
  &:active {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const ZoomPercentage = styled.div`
  color: white;
  display: flex;
  align-items: center;
  font-size: 14px;
  padding: 0 8px;
`;

const DocumentErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  text-align: center;
  color: var(--text-secondary);
  
  svg {
    font-size: 3rem;
    margin-bottom: 1rem;
    color: var(--warning);
  }
  
  h4 {
    margin: 0 0 0.5rem 0;
    color: var(--text-primary);
  }
  
  p {
    margin: 0 0 1rem 0;
  }
  
  button {
    margin: 0.5rem;
  }
`;

const EntityDetailsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1.5rem;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const EntityTableHeader = styled.th`
  background-color: #f8fafc;
  color: var(--text-primary);
  font-weight: 600;
  text-align: left;
  padding: 1rem;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.9rem;
`;

const EntityTableRow = styled.tr`
  &:nth-child(even) {
    background-color: #f9fafb;
  }
  
  &:hover {
    background-color: #f1f5f9;
  }
`;

const EntityTableCell = styled.td`
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e2e8f0;
  vertical-align: top;
`;

const EntityKeyCell = styled(EntityTableCell)`
  font-weight: 500;
  color: var(--text-secondary);
  width: 30%;
  min-width: 200px;
`;

const EntityValueCell = styled(EntityTableCell)`
  color: var(--text-primary);
  font-weight: 400;
`;

const NestedEntityTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 0.5rem;
  background: #f8fafc;
  border-radius: 4px;
  overflow: hidden;
`;

const NestedEntityRow = styled.tr`
  &:not(:last-child) {
    border-bottom: 1px solid #e2e8f0;
  }
`;

const NestedEntityKeyCell = styled.td`
  padding: 0.5rem 0.75rem;
  font-weight: 500;
  color: var(--text-secondary);
  width: 40%;
  font-size: 0.85rem;
`;

const NestedEntityValueCell = styled.td`
  padding: 0.5rem 0.75rem;
  color: var(--text-primary);
  font-size: 0.85rem;
`;

const ZoomableImage = ({ src, alt, onError }) => {
  const [scale, setScale] = useState(1);
  const [posX, setPosX] = useState(0);
  const [posY, setPosY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const handleZoomIn = () => {
    setScale(prevScale => Math.min(prevScale + 0.25, 5));
  };

  const handleZoomOut = () => {
    setScale(prevScale => Math.max(prevScale - 0.25, 0.5));
  };

  const handleReset = () => {
    setScale(1);
    setPosX(0);
    setPosY(0);
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - posX,
      y: e.clientY - posY
    });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setPosX(e.clientX - dragStart.x);
      setPosY(e.clientY - dragStart.y);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      setScale(prevScale => Math.min(prevScale + 0.1, 5));
    } else {
      setScale(prevScale => Math.max(prevScale - 0.1, 0.5));
    }
  };

  useEffect(() => {
    const handleMouseUpGlobal = () => {
      setIsDragging(false);
    };

    window.addEventListener('mouseup', handleMouseUpGlobal);
    return () => {
      window.removeEventListener('mouseup', handleMouseUpGlobal);
    };
  }, []);

  return (
    <ZoomableImageContainer onWheel={handleWheel}>
      <ZoomableImageWrapper
        scale={scale}
        posX={posX}
        posY={posY}
        isDragging={isDragging}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      >
        <ZoomableImg src={src} alt={alt} onError={onError} />
      </ZoomableImageWrapper>

      <ImageControls>
        <ImageControlButton onClick={handleZoomOut} title="Zoom out">
          <FiMinus />
        </ImageControlButton>
        <ZoomPercentage>{Math.round(scale * 100)}%</ZoomPercentage>
        <ImageControlButton onClick={handleZoomIn} title="Zoom in">
          <FiPlus />
        </ImageControlButton>
        <ImageControlButton onClick={handleReset} title="Reset view">
          <FiMaximize2 />
        </ImageControlButton>
      </ImageControls>
    </ZoomableImageContainer>
  );
};

// Communication card components
const CommunicationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
`;

const CommunicationCard = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  border: 1px solid var(--border-color);
`;

const CommunicationHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  cursor: pointer;
  background-color: ${props => props.isExpanded ? 'rgba(0, 102, 255, 0.05)' : 'white'};
  border-bottom: ${props => props.isExpanded ? '1px solid var(--border-color)' : 'none'};
  
  &:hover {
    background-color: rgba(0, 102, 255, 0.05);
  }
`;

const CommunicationInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const CommunicationIconContainer = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${props => {
    switch (props.source) {
      case 'email': return '#4285F4';
      case 'chat': return '#34A853';
      case 'call': return '#EA4335';
      case 'whatsapp': return '#25D366';
      case 'portal': return '#FF9900';
      case 'agent': return '#9C27B0';
      default: return '#757575';
    }
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
`;

const CommunicationDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const CommunicationTitle = styled.div`
  font-weight: 500;
  color: var(--text-primary);
`;

const CommunicationMeta = styled.div`
  font-size: 0.8rem;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CommunicationContent = styled.div`
  padding: 1rem;
  color: var(--text-primary);
  line-height: 1.5;
`;

const CommunicationAttachments = styled.div`
  padding: 0 1rem 1rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const AttachmentItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background-color: #f5f5f5;
  border-radius: 4px;
  font-size: 0.85rem;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #e0e0e0;
  }
`;

const CommunicationControls = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const CommunicationControl = styled.button`
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  
  &:hover {
    color: var(--primary);
  }
`;

// Helper function to get communication icon
const getCommunicationIcon = (source) => {
  switch (source) {
    case 'email': return <FiMail />;
    case 'chat': return <FiMessageSquare />;
    case 'call': return <FiPhone />;
    case 'whatsapp': return <FiSmartphone />;
    case 'portal': return <FiUsers />;
    case 'agent': return <FiUser />;
    default: return <FiMessageSquare />;
  }
};

// Helper function to format HTML content safely
const createMarkup = (htmlContent) => {
  return { __html: htmlContent };
};

const ClaimsHubDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // console.log('Component mounted, id from params:', id);

  const [activeTab, setActiveTab] = useState('decision');
  const [claim, setClaim] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAction, setSelectedAction] = useState('planning');
  // const [feedback, setFeedback] = useState(null);
  // const [remarks, setRemarks] = useState('');
  // const [addToFeedback, setAddToFeedback] = useState(false);
  // const [submittingFeedback, setSubmittingFeedback] = useState(false);
  // const [notification, setNotification] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [viewingDocument, setViewingDocument] = useState(null);
  const [documentError, setDocumentError] = useState(null);
  const [documentExpanded, setDocumentExpanded] = useState(false);
  const [overrideMode, setOverrideMode] = useState(false);
  const [newDecision, setNewDecision] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [submittingOverride, setSubmittingOverride] = useState(false);
  const [overrideNotification, setOverrideNotification] = useState(null);
  const [refEntity, setRefEntity] = useState(null);
  const [isTabDropdownOpen, setIsTabDropdownOpen] = useState(false);
  const [associatedEntityData, setAssociatedEntityData] = useState({});

  // Pipeline Execution Drawer state
  const [analyticsDrawerOpen, setAnalyticsDrawerOpen] = useState(false);
  const [executionResults, setExecutionResults] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [pipelineName, setPipelineName] = useState('');
  const [totalExecutionResults, setTotalExecutionResults] = useState(0);
  const [pipelineStatus, setPipelineStatus] = useState('');
  const [pollingInterval, setPollingInterval] = useState(null);
  const [pageNumberNode, setPageNumberNode] = useState(0);
  const [pageSizeNode] = useState(10);
  const [hasMorePages, setHasMorePages] = useState(true);

  // // State for expanded communication items
  // const [expandedCommunications, setExpandedCommunications] = useState({});

  // // State for expanded timeline items
  // const [expandedTimelineItems, setExpandedTimelineItems] = useState({});

  // Use a fallback for API base URL if not defined in environment

  // Utility function to merge new results with existing ones, avoiding duplicates
  const mergeUniqueResults = useCallback((currentResults, newResults, replaceAll = false) => {
    if (!newResults || newResults.length === 0) {
      return replaceAll ? [] : currentResults;
    }

    if (replaceAll) {
      return newResults;
    }

    const existingIds = new Set(currentResults.map(result => result.id));
    const uniqueNewResults = newResults.filter(result => !existingIds.has(result.id));

    return uniqueNewResults.length > 0 ? [...currentResults, ...uniqueNewResults] : currentResults;
  }, []);

  // // Get Claim Details
  useEffect(() => {

    if (!id) {
      // console.log('No ID found, skipping fetch');
      setLoading(false);
      return;
    }

    const fetchPipeline = async () => {
      try {
        // console.log('Starting fetch for ID:', id);
        setLoading(true);
        let filter = { id: +id };
        // console.log('Filter object:', filter);
        const pipelineData = await getPipelineRuns(filter);
        // console.log('Pipeline Data:', pipelineData);
        setClaim(pipelineData[0]);
      } catch (error) {
        console.error("Error fetching claim:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPipeline();
  }, [id]);

  // // Get Claim Details
  useEffect(() => {
    // console.log('useEffect triggered with Pipeline Load:', claim);

    if (!claim) {
      console.log('No Pipeline found, skipping fetch');
      setLoading(false);
      return;
    }

    const fetchEntityData = async () => {
      try {
        let userData = JSON.parse(localStorage.getItem('userData'));
        let entityTableName = claim?.entity_name.toLowerCase().split(' ').join('_')  + "_" + userData.companyCd.toLowerCase();

        let filter = { "id": claim?.entity_ref };
        const response = await listAllEntities(entityTableName, filter);
        setRefEntity(response[0] || []);
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchEntityData();
  }, [claim, id]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isTabDropdownOpen && !event.target.closest('.tab-dropdown-container')) {
        setIsTabDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isTabDropdownOpen]);

  // Fetch data for associated entities
  useEffect(() => {
    if (!claim?.associated_entities?.length) {
      return;
    }

    const fetchAssociatedEntitiesData = async () => {
      try {
        const entityDataMap = {};

        // Fetch data for each associated entity
        for (const entity of claim.associated_entities) {
          if (entity.entity_name && entity.entity_ref) {
            try {
              let userData = JSON.parse(localStorage.getItem('userData'));
              let entityTableName = entity.entity_name.toLowerCase().split(' ').join('_')  + "_" + userData.companyCd.toLowerCase();
              let filter = { "id": entity.entity_ref };
              const response = await listAllEntities(entityTableName, filter);
              entityDataMap[entity.entity_name] = response[0] || null;
            } catch (error) {
              console.error(`Error fetching data for entity ${entity.entity_name}:`, error);
              entityDataMap[entity.entity_name] = null;
            }
          }
        }

        setAssociatedEntityData(entityDataMap);
      } catch (error) {
        console.error('Error fetching associated entities data:', error);
      }
    };

    fetchAssociatedEntitiesData();
  }, [claim?.associated_entities]);

  // Initialize pipeline execution data
  useEffect(() => {
    if (claim) {

      setPipelineName(claim.name || 'Unnamed Pipeline');
      setPipelineStatus(claim.transaction_status || 'unknown');

      // Only fetch execution results if we have a pipeline code
      if (claim.code) {
        fetchExecutionResults();
      }
    }
  }, [claim]);

  // Cleanup polling interval on unmount
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  // // Get status badge color
  const getStatusColor = (status) => {
    // If status is undefined, treat it as completed
    if (!status) return 'active';

    switch (status) {
      case 'approved': return 'active';
      case 'rejected': return 'error';
      case 'pending': return 'warning';
      case 'verified': return 'active';
      case 'completed': return 'active';
      default: return 'default';
    }
  };

  // // Get timeline icon based on action type
  // const getTimelineIcon = (action) => {
  //   switch (action.action) {
  //     case 'Document Verification':
  //       return { icon: <FiFileText />, bg: '#0ea5e9', color: 'white' };
  //     case 'Policy Validation':
  //       return { icon: <FiClipboard />, bg: '#10b981', color: 'white' };
  //     case 'Fraud Detection':
  //       return { icon: <FiShield />, bg: '#f59e0b', color: 'white' };
  //     case 'Payment Calculation':
  //       return { icon: <FiDollarSign />, bg: '#0ea5e9', color: 'white' };
  //     case 'Human Review Escalation':
  //       return { icon: <FiUser />, bg: '#ef4444', color: 'white' };
  //     default:
  //       return { icon: <FiInfo />, bg: '#64748b', color: 'white' };
  //   }
  // };

  // // Get agent avatar color
  // const getAgentColor = (agentName) => {
  //   if (agentName.includes('Document')) return '#0ea5e9';
  //   if (agentName.includes('Policy')) return '#10b981';
  //   if (agentName.includes('Fraud')) return '#f59e0b';
  //   if (agentName.includes('Processing')) return '#8b5cf6';
  //   if (agentName.includes('Triage')) return '#ef4444';
  //   return '#64748b';
  // };

  // // Calculate progress percentage
  // const calculateProgress = () => {
  //   if (!claim || !claim.agentActionsTimeline || !claim.agentActionsTimeline.actions) return 0;
  //   const total = claim.agentActionsTimeline.actions.length;
  //   const completed = claim.agentActionsTimeline.actions.filter(action => !action.status || action.status === 'completed').length;
  //   return (completed / total) * 100;
  // };

  // // Get agent initials
  const getAgentInitials = (agentName) => {
    return agentName
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase();
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    });
  };
  // Capitalize
  const capitalize = (str) => {
    if (!str) return '';
    const strArr = str.split(' ');
    return strArr.map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };
  // filesize Formatter
  const fileSizeFormatter = (bytes, decimalPlaces = 2) => {
    if (bytes === 0) return "0 Bytes";

    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));

    return parseFloat((bytes / Math.pow(1024, i)).toFixed(decimalPlaces)) + " " + sizes[i];
  };

  // Format key name to sentence case
  const formatKeyName = (key) => {
    if (!key) return '';
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Filter entity data to exclude system fields
  const filterEntityData = (data) => {
    if (!data) return {};

    const excludeFields = ['id', 'company_cd', 'created_at', 'created_by', 'updated_at', 'updated_by'];
    const filtered = {};

    Object.keys(data).forEach(key => {
      if (!excludeFields.includes(key)) {
        filtered[key] = data[key];
      }
    });

    return filtered;
  };

  // Render entity data as nested tables
  const renderEntityValue = (value) => {
    if (value === null || value === undefined) {
      return <span style={{ fontStyle: 'italic', color: '#9ca3af' }}>Not provided</span>;
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
      return (
        <NestedEntityTable>
          <tbody>
            {Object.entries(value).map(([nestedKey, nestedValue]) => (
              <NestedEntityRow key={nestedKey}>
                <NestedEntityKeyCell>{formatKeyName(nestedKey)}</NestedEntityKeyCell>
                <NestedEntityValueCell>
                  {nestedValue === null || nestedValue === undefined ? (
                    <span style={{ fontStyle: 'italic', color: '#9ca3af' }}>Not provided</span>
                  ) : (
                    String(nestedValue)
                  )}
                </NestedEntityValueCell>
              </NestedEntityRow>
            ))}
          </tbody>
        </NestedEntityTable>
      );
    }

    return String(value);
  };

  // Function to detect if a string contains markdown
  const isMarkdown = (text) => {
    if (!text || typeof text !== 'string') return false;

    // Check for common markdown patterns
    const markdownPatterns = [
      /#{1,6}\s+/, // Headers
      /\*\*[^*]+\*\*/, // Bold
      /\*[^*]+\*/, // Italic
      /`[^`]+`/, // Inline code
      /```[\s\S]*?```/, // Code blocks
      /\[.+\]\(.+\)/, // Links
      /!\[.*\]\(.+\)/, // Images
      /^\s*[-*+]\s+/m, // Unordered lists
      /^\s*\d+\.\s+/m, // Ordered lists
      /^\s*>\s+/m, // Blockquotes
      /\|.*\|/, // Tables
      /---+/, // Horizontal rules
    ];

    return markdownPatterns.some(pattern => pattern.test(text));
  };

  // Enhanced renderEntityValue to handle markdown
  const renderEntityValueWithMarkdown = (value) => {
    if (value === null || value === undefined) {
      return <span style={{ fontStyle: 'italic', color: '#9ca3af' }}>Not provided</span>;
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
      return (
        <NestedEntityTable>
          <tbody>
            {Object.entries(value).map(([nestedKey, nestedValue]) => (
              <NestedEntityRow key={nestedKey}>
                <NestedEntityKeyCell>{formatKeyName(nestedKey)}</NestedEntityKeyCell>
                <NestedEntityValueCell>
                  {nestedValue === null || nestedValue === undefined ? (
                    <span style={{ fontStyle: 'italic', color: '#9ca3af' }}>Not provided</span>
                  ) : isMarkdown(String(nestedValue)) ? (
                    <MarkdownContainer style={{ margin: 0, padding: '0.5rem', fontSize: '0.85rem' }}>
                      <div className="markdown-content">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {String(nestedValue)}
                        </ReactMarkdown>
                      </div>
                    </MarkdownContainer>
                  ) : (
                    String(nestedValue)
                  )}
                </NestedEntityValueCell>
              </NestedEntityRow>
            ))}
          </tbody>
        </NestedEntityTable>
      );
    }

    const stringValue = String(value);
    if (isMarkdown(stringValue)) {
      return (
        <MarkdownContainer style={{ margin: 0, padding: '0.5rem', fontSize: '0.85rem' }}>
          <div className="markdown-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {stringValue}
            </ReactMarkdown>
          </div>
        </MarkdownContainer>
      );
    }

    return stringValue;
  };

  // // Function to handle feedback submission
  // const handleSubmitFeedback = async () => {
  //   if (!feedback) return;

  //   setSubmittingFeedback(true);

  //   try {
  //     // Mock API call - replace with actual API endpoint
  //     // await submitFeedback({
  //     //   claimId,
  //     //   feedback,
  //     //   remarks: addToFeedback ? remarks : '',
  //     //   timestamp: new Date().toISOString()
  //     // });

  //     // Simulate API call success
  //     await new Promise(resolve => setTimeout(resolve, 1000));

  //     // Show success notification
  //     setNotification({
  //       type: 'success',
  //       message: 'Feedback submitted successfully!'
  //     });

  //     console.log('Feedback submitted:', {
  //       claimId,
  //       feedback,
  //       remarks: addToFeedback ? remarks : '',
  //       timestamp: new Date().toISOString()
  //     });

  //     // Reset form after 2 seconds
  //     setTimeout(() => {
  //       setFeedback(null);
  //       setRemarks('');
  //       setAddToFeedback(false);
  //       setNotification(null);
  //     }, 2000);

  //   } catch (error) {
  //     console.error('Error submitting feedback:', error);
  //     // Show error notification
  //     setNotification({
  //       type: 'error',
  //       message: 'Failed to submit feedback. Please try again.'
  //     });
  //   } finally {
  //     setSubmittingFeedback(false);
  //   }
  // };

  // Handle override submission
  const handleOverrideSubmit = async () => {
    if (!newDecision || !overrideReason) {
      setOverrideNotification({
        type: 'error',
        message: 'Please select a decision and provide a reason for the override.'
      });
      return;
    }

    try {
      setSubmittingOverride(true);
      setOverrideNotification(null);

      // Call the API to update the claim status with the override reason
      await updateClaimStatus(id, newDecision, overrideReason);

      // Reload the claim data to reflect the changes
      const updatedClaim = await getClaimById(id);
      setClaim(updatedClaim);

      // Show success notification
      setOverrideNotification({
        type: 'success',
        message: 'Decision has been successfully overridden.'
      });

      // Reset the override form
      setOverrideMode(false);
      setNewDecision('');
      setOverrideReason('');
    } catch (error) {
      console.error('Error submitting override:', error);
      setOverrideNotification({
        type: 'error',
        message: 'Failed to override decision. Please try again.'
      });
    } finally {
      setSubmittingOverride(false);
    }
  };

  // // Toggle expanded view
  // const toggleExpanded = () => {
  //   setExpanded(!expanded);
  // };

  // // Toggle document expanded view
  const toggleDocumentExpanded = () => {
    setDocumentExpanded(!documentExpanded);
  };

  // Pipeline Execution Drawer handlers
  const refreshResults = () => {
    // Refresh execution results logic
    if (claim?.code) {
      fetchExecutionResults();
    }
  };

  const handleResumePipeline = async () => {
    try {
      setIsRunning(true);
      // Use the same resume pipeline logic as PipelineAnalytics
      if (claim?.code) {
        await resumePipeline(claim.code);
        console.log('Pipeline resumed successfully:', claim.code);
        // Refresh results after resuming
        fetchExecutionResults();
      }
    } catch (error) {
      console.error('Error resuming pipeline:', error);
    } finally {
      setIsRunning(false);
    }
  };

  const handlePaginationChange = useCallback(async (event) => {
    // console.log('handlePaginationChange called with event:', event);
    // console.log('Current state - isLoadingMore:', isLoadingMore, 'hasMorePages:', hasMorePages);

    if (event === 'next' && !isLoadingMore && hasMorePages) {
      setIsLoadingMore(true);
      const nextPage = pageNumberNode + 1;

      try {
        if (!claim?.code) {
          setIsLoadingMore(false);
          return;
        }

        // First, check total count to see if there are more items - use claim.code consistently
        const totalCount = await fetchCountByFilter('node_execution', { pipeline_id: claim.pipeline_id });
        const currentItemsCount = executionResults.length;

        // console.log(`Total items in DB: ${totalCount.count}, Currently loaded: ${currentItemsCount}`);

        // If we already have all items, don't fetch more
        if (currentItemsCount >= totalCount.count) {
          console.log('All items already loaded, no more pagination needed');
          setHasMorePages(false);
          setIsLoadingMore(false);
          return;
        }

        const filter = { pipeline_id: claim.pipeline_id };
        const sort = { created_at: "ASC" };

        // Fetch next page with skip and limit
        const response = await getNodesForPipeline(
          filter,
          sort,
          `?skip=${nextPage * pageSizeNode}&limit=${pageSizeNode}`
        );

        // console.log('Pagination API response:', response);

        if (response && response.length > 0) {
          // Use utility function to merge unique results
          setExecutionResults(prevResults => {
            const newResults = mergeUniqueResults(prevResults, response);
            // console.log(`Added ${response.length} new items, total now: ${newResults.length}`);
            return newResults;
          });
          setPageNumberNode(nextPage);

          // Check if we've reached the end based on total count
          const newTotalLoaded = currentItemsCount + response.length;
          if (newTotalLoaded >= totalCount.count || response.length < pageSizeNode) {
            // console.log('Reached end of data - no more pages available');
            setHasMorePages(false);
          }
        } else {
          // No data returned, reached the end
          // console.log('No more data returned from API');
          setHasMorePages(false);
        }
      } catch (error) {
        console.error('Error loading more results:', error);
      } finally {
        setIsLoadingMore(false);
      }
    } else {
      // console.log('Pagination skipped - event:', event, 'isLoadingMore:', isLoadingMore, 'hasMorePages:', hasMorePages);
    }
  }, [claim?.code, pageNumberNode, pageSizeNode, isLoadingMore, hasMorePages, mergeUniqueResults, executionResults.length]);

  const fetchExecutionResults = async () => {
    try {
      if (claim?.code) {
        // Reset pagination and fetch first page
        setPageNumberNode(0);
        setIsLoadingMore(false);
        setHasMorePages(true);

        // Use getNodesForPipeline like in PipelineAnalytics - use claim.code consistently
        const getTotalCount = await fetchCountByFilter('node_execution', { pipeline_id: claim.pipeline_id });
        setTotalExecutionResults(getTotalCount || 0);
        const filter = { pipeline_id: claim.pipeline_id };
        const sort = { created_at: "ASC" };
        let queryParams = `?skip=0&limit=${pageSizeNode}`;
        if (claim?.transaction_status === 'CLOSED') {
          queryParams = `?skip=0&limit=${getTotalCount}`;

        }

        const response = await getNodesForPipeline(filter, sort, queryParams);
        // console.log('Pipeline execution results:', response);

        const uniqueResults = mergeUniqueResults([], response, true); // Replace all with new results

        setExecutionResults(uniqueResults);

        // Check if more pages based on total count
        const hasMore = uniqueResults.length < getTotalCount;
        setHasMorePages(hasMore);

        // console.log(`Initial load: ${uniqueResults.length} items loaded, ${getTotalCount.count} total available, hasMore: ${hasMore}`);
      } else {
        console.log('No claim code available for fetching execution results');
        setExecutionResults([]);
        setTotalExecutionResults(0);
      }
    } catch (error) {
      console.error('Error fetching execution results:', error);
      setExecutionResults([]);
      setTotalExecutionResults(0);
    }
  };

  // // Toggle communication expansion
  // const toggleCommunication = (id) => {
  //   setExpandedCommunications(prev => ({
  //     ...prev,
  //     [id]: !prev[id]
  //   }));
  // };

  // // Toggle timeline item expansion
  // const toggleTimelineItem = (id) => {
  //   setExpandedTimelineItems(prev => ({
  //     ...prev,
  //     [id]: !prev[id]
  //   }));
  // };

  // // Keyboard shortcut for expand/collapse
  // useEffect(() => {
  //   const handleKeyDown = (e) => {
  //     // Alt+E shortcut for expand/collapse
  //     if (e.altKey && e.shiftKey && e.key === 'E') {
  //       toggleExpanded();
  //     }

  //     // Escape key to collapse when expanded
  //     if (e.key === 'Escape' && expanded) {
  //       setExpanded(false);
  //     }
  //   };

  //   window.addEventListener('keydown', handleKeyDown);

  //   // Clean up event listener
  //   return () => {
  //     window.removeEventListener('keydown', handleKeyDown);
  //   };
  // }, [expanded]);

  // // Handle attachment click
  // const handleAttachmentClick = (attachment) => {
  //   // Find the document in the claim documents that matches the attachment link
  //   const matchingDoc = claim.documents.find(doc =>
  //     doc.link && doc.link.includes(attachment.documentLink)
  //   );

  //   if (matchingDoc) {
  //     handleDocumentClick(matchingDoc);
  //   } else {
  //     // Create a temporary document object if no matching document found
  //     const tempDoc = {
  //       name: attachment.documentName,
  //       link: attachment.documentLink,
  //       type: 'Attachment'
  //     };
  //     handleDocumentClick(tempDoc);
  //   }
  // };

  // Handle document click
  const handleDocumentClick = async (doc) => {
    // console.log("Document clicked:", doc);
    setDocumentError(null);

    if (!doc.url) {
      // console.log("No link found in document");
      setDocumentError("This document doesn't have a viewable file attached.");
      setViewingDocument(doc);
      return;
    }

    try {

      const response = await downloadImage(doc.url)

      const docWithUrl = {
        ...doc,
        blobUrl: response
      };
      setViewingDocument(docWithUrl);
    } catch (error) {
      console.error("Error fetching document:", error);
      setDocumentError(`Error loading document: ${error.message}`);
      setViewingDocument(doc);
    }
  };

  // // Get document icon based on file type
  const getDocumentIcon = (filename) => {
    if (!filename) return <FiFile />;

    const extension = filename.split('.').pop().toLowerCase();

    switch (extension) {
      case 'pdf':
        return <FiFileText />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'svg':
        return <FiImage />;
      case 'mp4':
      case 'webm':
      case 'mov':
        return <FiVideo />;
      case 'mp3':
      case 'wav':
      case 'ogg':
        return <FiMusic />;
      default:
        return <FiFile />;
    }
  };

  const getDocumentFileType = (filename) => {
    if (!filename) return '-';
    const extension = filename.split('.').pop().toLowerCase();
    return capitalize(extension);
  };

  // if (loading) {
  //   return (
  //     <PageContainer>
  //       <PageHeader>
  //         <PageTitle><PageIcon>⚕</PageIcon> Loading Claim Details...</PageTitle>
  //       </PageHeader>
  //     </PageContainer>
  //   );
  // }


  if (loading) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle><PageIcon>⚕</PageIcon> Loading Pipeline Details...</PageTitle>
        </PageHeader>
      </PageContainer>
    );
  }

  if (!claim) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle><PageIcon>⚕</PageIcon> Pipeline Not Found</PageTitle>
        </PageHeader>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <GlobalStyle />
      <PageHeader>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Button onClick={() => navigate('/new-business-hub')}>
            <FiArrowLeft /> Back
          </Button>
          <PageTitle><PageIcon><FontAwesomeIcon icon={faHurricane} /> </PageIcon> {claim?.name || 'Loading...'} #{claim?.id || ''}</PageTitle>
          {claim?.transaction_status && (
            <StatusBadge status={getStatusColor(claim?.transaction_status)}>
              {claim?.transaction_status?.charAt(0).toUpperCase() + claim?.transaction_status?.slice(1)}
            </StatusBadge>
          )}
        </div>
        <div>
          {/* <ActionButtonsContainer>

            <ActionButton
              active={selectedAction === 'actions'}
              onClick={() => setSelectedAction('actions')}
              data-tooltip="Actions"
            >
              <FiActivity />
            </ActionButton>
            <ActionButton
              active={selectedAction === 'planning'}
              onClick={() => {
                setSelectedAction('planning');
                setAnalyticsDrawerOpen(true);
              }}
              data-tooltip="Pipeline Execution"
            >
              <FiBarChart2 />
            </ActionButton>
            <ActionButton
              active={selectedAction === 'adjudication'}
              onClick={() => setSelectedAction('adjudication')}
              data-tooltip="Adjudication Report"
            >
              <FiList />
            </ActionButton>
            <ActionButton
              active={selectedAction === 'sources'}
              onClick={() => setSelectedAction('sources')}
              data-tooltip="Sources"
            >
              <FiFolder />
            </ActionButton>
          </ActionButtonsContainer> */}
        </div>
      </PageHeader>
      <ClaimDetailsContainer>
        {/* Left Side - Claim Details */}
        <DetailsSection hidden={expanded}>
          {viewingDocument ? (
            <DocumentViewerContainer isExpanded={documentExpanded}>
              <DocumentViewerHeader>
                <DocumentViewerTitle>
                  {getDocumentIcon(viewingDocument.url)} {viewingDocument.name}
                </DocumentViewerTitle>
                <DocumentActions>
                  {/* <DocumentAction
                    title="Download document"
                    onClick={() => {
                      if (viewingDocument.blobUrl) {
                        window.open(viewingDocument.blobUrl, '_blank');
                      } else if (viewingDocument.url) {
                        // window.open(`${apiBaseUrl}claims/${claimId}/fileId/${viewingDocument.link}`, '_blank');
                      }
                    }}
                  >
                    <FiDownload />
                  </DocumentAction> */}
                  <DocumentAction
                    title={documentExpanded ? "Exit fullscreen" : "Fullscreen"}
                    onClick={toggleDocumentExpanded}
                  >
                    {documentExpanded ? <FiMinimize /> : <FiMaximize />}
                  </DocumentAction>
                  <DocumentAction
                    title="Close document"
                    onClick={() => {
                      setDocumentExpanded(false);
                      setViewingDocument(null);
                    }}
                  >
                    <FiX />
                  </DocumentAction>
                </DocumentActions>
              </DocumentViewerHeader>

              <DocumentViewerContent>
                {documentError ? (
                  <DocumentErrorContainer>
                    <FiAlertCircle />
                    <h4>Unable to display document</h4>
                    <p>{documentError}</p>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                      <Button
                        onClick={() => {
                          if (viewingDocument.blobUrl) {
                            window.open(viewingDocument.blobUrl, '_blank');
                          } else if (viewingDocument.url) {
                            // window.open(`${apiBaseUrl}claims/${claimId}/fileId/${viewingDocument.url}`, '_blank');
                          }
                        }}
                        style={{ marginBottom: '0.5rem' }}
                      >
                        Download File
                      </Button>
                      <Button
                        onClick={() => setViewingDocument(null)}
                        variant="secondary"
                      >
                        Return to documents
                      </Button>
                    </div>
                  </DocumentErrorContainer>
                ) : (
                  (() => {
                    // Check if there's no link
                    if (!viewingDocument.url) {
                      return (
                        <DocumentErrorContainer>
                          <FiAlertCircle />
                          <h4>No preview available</h4>
                          <p>This document doesn't have a viewable file attached.</p>
                          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                            <Button
                              onClick={() => {
                                if (viewingDocument.blobUrl) {
                                  window.open(viewingDocument.blobUrl, '_blank');
                                } else if (viewingDocument.url) {
                                  // window.open(`${apiBaseUrl}claims/${claimId}/fileId/${viewingDocument.link}`, '_blank');
                                }
                              }}
                              style={{ marginBottom: '0.5rem' }}
                            >
                              Download File
                            </Button>
                            <Button
                              onClick={() => setViewingDocument(null)}
                              variant="secondary"
                            >
                              Return to documents
                            </Button>
                          </div>
                        </DocumentErrorContainer>
                      );
                    }

                    let extension = '';
                    if (viewingDocument.name && viewingDocument.name.includes('.')) {
                      extension = viewingDocument.name.split('.').pop().toLowerCase();
                    } else if (viewingDocument.url && viewingDocument.url.includes('.')) {
                      extension = viewingDocument.url.split('.').pop().toLowerCase();
                    } else if (viewingDocument.type) {
                      const contentType = viewingDocument.type.toLowerCase();
                      if (contentType.includes('pdf')) {
                        extension = 'pdf';
                      } else if (contentType.includes('image')) {
                        extension = 'jpg';
                      } else if (contentType.includes('video')) {
                        extension = 'mp4';
                      } else if (contentType.includes('audio')) {
                        extension = 'mp3';
                      }
                    }

                    console.log("Document extension detected:", extension);
                    console.log("Document viewing:", viewingDocument);

                    if (extension === 'pdf') {
                      return (
                        <>
                          <DocumentEmbed
                            src={viewingDocument.blobUrl}
                            type="application/pdf"
                            title={viewingDocument.name}
                          />
                        </>
                      );
                    }

                    switch (extension) {
                      case 'jpg':
                      case 'jpeg':
                      case 'png':
                      case 'gif':
                      case 'svg':
                        return (
                          <ZoomableImage
                            src={viewingDocument.blobUrl}
                            alt={viewingDocument.name}
                            onError={() => setDocumentError("Failed to load image.")}
                          />
                        );
                      case 'mp4':
                      case 'webm':
                      case 'mov':
                        return (
                          <DocumentVideo controls>
                            <source src={viewingDocument.blobUrl} type={`video/${extension}`} />
                            Your browser does not support the video tag.
                          </DocumentVideo>
                        );
                      case 'mp3':
                      case 'wav':
                      case 'ogg':
                        return (
                          <DocumentAudio controls>
                            <source src={viewingDocument.blobUrl} type={`audio/${extension}`} />
                            Your browser does not support the audio tag.
                          </DocumentAudio>
                        );
                      default:
                        return (
                          <DocumentErrorContainer>
                            <FiFile />
                            <h4>Preview not available</h4>
                            <p>This file type ({extension}) cannot be previewed directly.</p>
                            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                              <Button
                                onClick={() => {
                                  if (viewingDocument.blobUrl) {
                                    window.open(viewingDocument.blobUrl, '_blank');
                                  } else if (viewingDocument.url) {
                                    // window.open(`${apiBaseUrl}claims/${claimId}/fileId/${viewingDocument.url}`, '_blank');
                                  }
                                }}
                                style={{ marginBottom: '0.5rem' }}
                              >
                                Download File
                              </Button>
                              <Button
                                onClick={() => setViewingDocument(null)}
                                variant="secondary"
                              >
                                Return to documents
                              </Button>
                            </div>
                          </DocumentErrorContainer>
                        );
                    }
                  })()
                )}
              </DocumentViewerContent>
            </DocumentViewerContainer>

          ) : (
            <>
              {/* Conditional rendering: dropdown if more than 3 tabs, otherwise regular tabs */}
              {claim?.associated_entities?.length > 3 ? (
                <TabDropdownContainer className="tab-dropdown-container">
                  <TabDropdownTrigger
                    onClick={() => setIsTabDropdownOpen(!isTabDropdownOpen)}
                    $active={isTabDropdownOpen}
                  >
                    <FiFilePlus />
                    {activeTab === 'decision' ? 'Summary' :
                      activeTab === 'entity_name' ? claim?.entity_name :
                        activeTab === 'documents' ? 'Documents' :
                          claim?.associated_entities?.find((_, index) => activeTab === `entity-${index}`)?.entity_name || 'Select Tab'}
                    {isTabDropdownOpen ? <FiChevronUp /> : <FiChevronDown />}
                  </TabDropdownTrigger>
                  {isTabDropdownOpen && (
                    <TabDropdownMenu>
                      <TabDropdownItem
                        $active={activeTab === 'decision'}
                        onClick={() => { setActiveTab('decision'); setIsTabDropdownOpen(false); }}
                      >
                        <FiFilePlus /> Summary
                      </TabDropdownItem>
                      <TabDropdownItem
                        $active={activeTab === 'entity_name'}
                        onClick={() => { setActiveTab('entity_name'); setIsTabDropdownOpen(false); }}
                      >
                        <FiClipboard /> {claim?.entity_name}
                      </TabDropdownItem>
                      {claim?.associated_entities?.map((entity, index) => (
                        <TabDropdownItem
                          key={`entity-${index}`}
                          $active={activeTab === `entity-${index}`}
                          onClick={() => { setActiveTab(`entity-${index}`); setIsTabDropdownOpen(false); }}
                        >
                          <FiFileText /> {entity.entity_name}
                        </TabDropdownItem>
                      ))}
                      <TabDropdownItem
                        $active={activeTab === 'documents'}
                        onClick={() => { setActiveTab('documents'); setIsTabDropdownOpen(false); }}
                      >
                        <FiFileText /> Documents
                      </TabDropdownItem>
                    </TabDropdownMenu>
                  )}
                </TabDropdownContainer>
              ) : (
                <TabsContainer>
                  <Tab active={activeTab === 'decision'} onClick={() => setActiveTab('decision')}>
                    <FiFilePlus /> Summary
                  </Tab>
                  <Tab active={activeTab === 'entity_name'} onClick={() => setActiveTab('entity_name')}>
                    <FiClipboard /> {claim?.entity_name}
                  </Tab>

                  {/* Dynamic tabs for associated entities */}
                  {claim?.associated_entities?.map((entity, index) => (
                    <Tab
                      key={`entity-${index}`}
                      active={activeTab === `entity-${index}`}
                      onClick={() => setActiveTab(`entity-${index}`)}
                    >
                      <FiFileText /> {entity.entity_name}
                    </Tab>
                  ))}

                  <Tab active={activeTab === 'documents'} onClick={() => setActiveTab('documents')}>
                    <FiFileText /> Documents
                  </Tab>
                </TabsContainer>
              )}
              <TabContent>
                {activeTab === 'decision' && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                    style={{ padding: '0' }}
                  >
                    {/* <CardTitle>Decision Summary</CardTitle> */}
                    {claim?.transaction_status !== 'pending' && (
                      <div style={{ marginBottom: '1rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                          <div style={{ fontWeight: '500' }}>Decision taken by Agent:</div>
                          <select
                            disabled
                            style={{
                              padding: '0.5rem',
                              borderRadius: '4px',
                              border: '1px solid var(--border-color)',
                              backgroundColor: '#f5f5f5'
                            }}
                          >
                            <option value={claim?.transaction_status}>{claim?.transaction_status?.charAt(0).toUpperCase() + claim?.transaction_status?.slice(1)}</option>
                          </select>
                          <Button
                            onClick={() => {
                              if (window.confirm('Are you sure you want to override the agent decision?')) {
                                setOverrideMode(true);
                              }
                            }}
                          >
                            Override
                          </Button>
                        </div>

                        {overrideMode && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            transition={{ duration: 0.3 }}
                          >
                            <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
                              <div style={{ marginBottom: '0.5rem', fontWeight: '500' }}>New Decision:</div>
                              <select
                                value={newDecision}
                                onChange={(e) => setNewDecision(e.target.value)}
                                style={{
                                  padding: '0.5rem',
                                  borderRadius: '4px',
                                  border: '1px solid var(--border-color)',
                                  marginBottom: '0.5rem',
                                  width: '100%'
                                }}
                              >
                                <option value="">Select a decision</option>
                                <option value="CLOSED">Approved</option>
                                <option value="INPROGRESS">In  Progress</option>
                              </select>

                              <div style={{ marginBottom: '0.5rem', fontWeight: '500' }}>Reason for override:</div>
                              <RemarksTextarea
                                value={overrideReason}
                                onChange={(e) => setOverrideReason(e.target.value)}
                                placeholder="Please provide a detailed reason for this override..."
                              />

                              <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                                <Button
                                  primary
                                  onClick={handleOverrideSubmit}
                                  disabled={!newDecision || !overrideReason || submittingOverride}
                                >
                                  {submittingOverride ? 'Submitting...' : 'Submit Override'}
                                </Button>
                                <Button onClick={() => setOverrideMode(false)}>
                                  Cancel
                                </Button>
                              </div>

                              {overrideNotification && (
                                <NotificationContainer type={overrideNotification.type}>
                                  {overrideNotification.type === 'success' ? <FiCheckCircle /> : <FiAlertCircle />}
                                  {overrideNotification.message}
                                </NotificationContainer>
                              )}
                            </div>
                          </motion.div>
                        )}
                      </div>
                    )}
                    <MarkdownContainer>
                      <div className="markdown-content">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {claim?.summary || '*No decision summary available.*'}
                        </ReactMarkdown>
                      </div>
                    </MarkdownContainer>
                  </motion.div>
                )}
                {activeTab === 'entity_name' && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <CardTitle>{claim?.entity_name || '*No entity name available.*'}</CardTitle>

                    {refEntity ? (
                      <EntityDetailsTable>
                        <tbody>
                          {Object.entries(filterEntityData(refEntity)).map(([key, value]) => (
                            <EntityTableRow key={key}>
                              <EntityKeyCell>{formatKeyName(key)}</EntityKeyCell>
                              <EntityValueCell>
                                {renderEntityValue(value)}
                              </EntityValueCell>
                            </EntityTableRow>
                          ))}
                        </tbody>
                      </EntityDetailsTable>
                    ) : (
                      <div style={{
                        padding: '2rem',
                        textAlign: 'center',
                        color: 'var(--text-secondary)',
                        fontStyle: 'italic'
                      }}>
                        Loading entity details...
                      </div>
                    )}
                  </motion.div>
                )}

                {/* Dynamic tab content for associated entities */}
                {claim?.associated_entities?.map((entity, index) => (
                  activeTab === `entity-${index}` && (
                    <motion.div
                      key={`entity-content-${index}`}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      <CardTitle>{entity.entity_name}</CardTitle>

                      {associatedEntityData[entity.entity_name] ? (
                        <EntityDetailsTable>
                          <tbody>
                            {Object.entries(filterEntityData(associatedEntityData[entity.entity_name])).map(([key, value]) => (
                              <EntityTableRow key={key}>
                                <EntityKeyCell>{formatKeyName(key)}</EntityKeyCell>
                                <EntityValueCell>
                                  {renderEntityValueWithMarkdown(value)}
                                </EntityValueCell>
                              </EntityTableRow>
                            ))}
                          </tbody>
                        </EntityDetailsTable>
                      ) : (
                        <div style={{
                          padding: '2rem',
                          textAlign: 'center',
                          color: 'var(--text-secondary)',
                          fontStyle: 'italic'
                        }}>
                          Loading {entity.entity_name.toLowerCase()} details...
                        </div>
                      )}
                    </motion.div>
                  )
                ))}

                {activeTab === 'documents' && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <CardTitle>Submitted Documents</CardTitle>
                    <DocumentList>
                      {claim.associated_docs.map((doc, index) => (
                        <DocumentItem
                          key={index}
                          hasLink={!!doc.url}
                          onClick={() => handleDocumentClick(doc)}
                        >
                          <DocumentIcon>
                            {getDocumentIcon(doc.url)}
                          </DocumentIcon>
                          <DocumentInfo>
                            <DocumentTitle>{doc.file_name}</DocumentTitle>
                            <DocumentMeta>{getDocumentFileType(doc.file_name)} • {fileSizeFormatter(doc.size)} • {formatDate(doc.updated_at)}</DocumentMeta>
                          </DocumentInfo>
                          {/* <StatusBadge status={getStatusColor(doc.status)}>
                            {capitalize(doc.status)}
                          </StatusBadge> */}
                        </DocumentItem>
                      ))}
                    </DocumentList>
                  </motion.div>
                )}
              </TabContent>
            </>
          )}
        </DetailsSection>
        {/* Right Side - Actions Panel */}
        <ActionsSection expanded={expanded}>
          <div className="valStream-audit">
            <PipelineExecutionDrawer
              isOpen={true}
              onClose={() => setSelectedAction('actions')}
              onRefresh={refreshResults}
              onResume={handleResumePipeline}
              onPaginationChange={handlePaginationChange}
              executionResults={executionResults}
              isRunning={isRunning}
              isLoadingMore={isLoadingMore}
              pipelineName={pipelineName}
              totalExecutionResults={totalExecutionResults}
              pipelineStatus={pipelineStatus}
              pollingInterval={pollingInterval}
            />
          </div>
        </ActionsSection>
      </ClaimDetailsContainer>
    </PageContainer>
  );
};

const GlobalStyle = createGlobalStyle`
  .markdown-content table {
    border-collapse: collapse;
    width: 100%;
    margin: 1rem 0;
    font-size: 0.9rem;
  }
  .markdown-content{
    font-size: 1.2rem;
    line-height: 1.9rem
  }  
  
  .markdown-content th {
    background-color: var(--bg-highlight);
    color: var(--text-primary);
    font-weight: 600;
    text-align: left;
    padding: 0.75rem;
    border: 1px solid var(--border-color);
  }
  
  .markdown-content td {
    padding: 0.75rem;
    border: 1px solid var(--border-color);
  }
  
  .markdown-content tr:nth-child(even) {
    background-color: rgba(0, 0, 0, 0.03);
  }
  
  .markdown-content code {
    background-color: rgba(0, 0, 0, 0.05);
    padding: 0.2rem 0.4rem;
    border-radius: 0.25rem;
    font-family: monospace;
  }
  
  .markdown-content blockquote {
    border-left: 4px solid var(--primary-color);
    padding-left: 1rem;
    margin-left: 0;
    color: var(--text-secondary);
  }
  
  .markdown-content ul, .markdown-content ol {
    padding-left: 2rem;
  }
`;

export default ClaimsHubDetails;
